import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Shared plumbing for plugin-config pages.
 *
 * Two axes of writable state:
 *   - `moduleConfig`: partial patch into moduleConfigs[pluginKey]
 *   - `scalars`:      top-level scalar column patch (welcomeChannelId, logChannelId, ...)
 *
 * Both get merged with the existing state before we PATCH to avoid wiping
 * other plugins' configs (Prisma JSON updates replace the whole value).
 */

export interface Settings {
  guildId: string;
  prefix: string;
  language: string;
  moduleConfigs: Record<string, Record<string, unknown>>;
}

export interface SavePayload {
  moduleConfig?: Record<string, unknown>;
  scalars?: Record<string, unknown>;
}

export function useConfigPage(pluginKey: string) {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['settings', guildId],
    queryFn: () => api<{ settings: Settings }>(`/api/guilds/${guildId}/settings`),
    enabled: !!guildId,
  });

  const saveMut = useMutation({
    // Only this page's module goes out — the API merges it into the stored
    // moduleConfigs, so a stale cache can't overwrite what others changed
    // in other modules meanwhile.
    mutationFn: async (payload: SavePayload) => {
      const existing = settingsQuery.data?.settings.moduleConfigs ?? {};
      const own = payload.moduleConfig
        ? { [pluginKey]: { ...(existing[pluginKey] ?? {}), ...payload.moduleConfig } }
        : undefined;
      return api(`/api/guilds/${guildId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ ...(own ? { moduleConfigs: own } : {}), ...(payload.scalars ?? {}) }),
      });
    },
    // ['guildSettings'] feeds the ServerLayout sidebar (configured-dots).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', guildId] });
      qc.invalidateQueries({ queryKey: ['guildSettings', guildId] });
    },
  });

  return {
    guildId: guildId!,
    settingsQuery,
    saveMut,
    currentConfig: (settingsQuery.data?.settings.moduleConfigs?.[pluginKey] ??
      {}) as Record<string, unknown>,
  };
}

export function BackLink({ guildId }: { guildId: string }) {
  return (
    <Link
      to={`/server/${guildId}`}
      className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
    </Link>
  );
}
